import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import SupportCasesPanel from '../components/admin/SupportCasesPanel';
import CancelledSubscriptionsPanel from '../components/admin/CancelledSubscriptionsPanel';
import NotificationsPanel from '../components/admin/NotificationsPanel';
import AffiliatesPanel from '../components/admin/AffiliatesPanel';
import AdminCancelUserDialog from '../components/admin/AdminCancelUserDialog';
import {
  Shield, Users, Trash2, CheckCircle,
  XCircle, Loader2, ArrowLeft, RefreshCw, DollarSign,
  TrendingUp, X, Calendar, LogIn, Package,
  AlertTriangle, Activity, Search, Stethoscope, RotateCcw,
  Terminal, Download, LogOut, UserX, UserCheck, CreditCard, Gift, Mail, LifeBuoy, UserMinus, Bell, Handshake, Ban, Lock, Unlock
} from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const AdminPanel = () => {
  const navigate = useNavigate();
  const { user, authToken, isAdmin, logout, startImpersonation, isImpersonating } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Health data
  const [healthData, setHealthData] = useState([]);
  const [loadingHealth, setLoadingHealth] = useState(false);

  // Revenue view state
  const [selectedUser, setSelectedUser] = useState(null);
  const [userRevenue, setUserRevenue] = useState(null);
  const [loadingRevenue, setLoadingRevenue] = useState(false);

  // Diagnose state
  const [diagnosing, setDiagnosing] = useState(null);
  const [diagnoseResult, setDiagnoseResult] = useState(null);

  // Force sync state
  const [syncingUser, setSyncingUser] = useState(null);

  // Impersonating state
  const [impersonating, setImpersonating] = useState(null);

  // Plan assignment state
  const [assigningPlan, setAssigningPlan] = useState(null);

  // Safe cancellation (Stripe first) — see AdminCancelUserDialog
  const [cancelTarget, setCancelTarget] = useState(null);
  const PLAN_TIERS = ['trial', 'entrepreneur', 'pro', 'elite'];

  // Free access whitelist (pre-approve an email for free entrepreneur-tier access before they sign up)
  const [pendingGrants, setPendingGrants] = useState([]);
  const [grantEmail, setGrantEmail] = useState('');
  const [creatingGrant, setCreatingGrant] = useState(false);
  const [deletingGrant, setDeletingGrant] = useState(null);

  // Item check state
  const [checkItemEmail, setCheckItemEmail] = useState('');
  const [checkItemId, setCheckItemId] = useState('');
  const [checkingItem, setCheckingItem] = useState(false);
  const [checkItemResult, setCheckItemResult] = useState(null);

  // MongoDB query explorer state
  const [queryCollection, setQueryCollection] = useState('');
  const [queryOperation, setQueryOperation] = useState('find');
  const [queryBody, setQueryBody] = useState('{\n  "filter": {},\n  "limit": 50\n}');
  const [queryRunning, setQueryRunning] = useState(false);
  const [queryResult, setQueryResult] = useState(null);
  const [queryError, setQueryError] = useState('');
  const [queryViewMode, setQueryViewMode] = useState('table'); // 'table' | 'json'

  // Tab state
  const [activeTab, setActiveTab] = useState('users');

  useEffect(() => {
    if (!isAdmin && !loading) {
      toast.error('Acceso denegado');
      navigate('/dashboard');
    }
  }, [isAdmin, loading, navigate]);

  const headers = { Authorization: `Bearer ${authToken}` };

  const getQueryPlaceholder = (op) => {
    const placeholders = {
      find:      '{\n  "filter": {},\n  "projection": null,\n  "sort": {"_id": -1},\n  "limit": 50\n}',
      aggregate: '[\n  {"$match": {}},\n  {"$limit": 50}\n]',
      count:     '{\n  "filter": {}\n}',
      distinct:  '{\n  "field": "status",\n  "filter": {}\n}',
    };
    return placeholders[op] || '';
  };

  const handleOperationChange = (op) => {
    setQueryOperation(op);
    setQueryBody(getQueryPlaceholder(op));
    setQueryResult(null);
    setQueryError('');
  };

  const handleRunQuery = async () => {
    if (!queryCollection.trim()) { setQueryError('Ingresa el nombre de la colección'); return; }
    let parsed;
    try {
      parsed = JSON.parse(queryBody);
    } catch (e) {
      setQueryError(`JSON inválido: ${e.message}`);
      return;
    }
    setQueryRunning(true);
    setQueryError('');
    setQueryResult(null);
    try {
      const payload = { collection: queryCollection.trim(), operation: queryOperation };
      if (queryOperation === 'aggregate' && Array.isArray(parsed)) {
        payload.pipeline = parsed;
      } else {
        Object.assign(payload, parsed);
      }
      const res = await axios.post(`${API_BASE_URL}/api/admin/query`, payload, { headers });
      setQueryResult(res.data);
    } catch (err) {
      setQueryError(err.response?.data?.detail || err.message || 'Error desconocido');
    }
    setQueryRunning(false);
  };

  const downloadQueryResult = (format) => {
    if (!queryResult?.results?.length) return;
    const data = queryResult.results;
    if (format === 'json') {
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `query_${queryCollection}_${Date.now()}.json`; a.click();
      URL.revokeObjectURL(url);
    } else {
      const keys = [...new Set(data.flatMap(Object.keys))];
      const csv = [
        keys.join(','),
        ...data.map(row => keys.map(k => {
          const v = row[k];
          const s = v === null || v === undefined ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v);
          return `"${s.replace(/"/g, '""')}"`;
        }).join(','))
      ].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `query_${queryCollection}_${Date.now()}.csv`; a.click();
      URL.revokeObjectURL(url);
    }
  };

  const fetchData = async () => {
    if (!authToken || isImpersonating) return;
    setLoading(true);
    try {
      const [statsRes, usersRes, grantsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/admin/stats`, { headers }),
        axios.get(`${API_BASE_URL}/api/admin/users`, { headers }),
        axios.get(`${API_BASE_URL}/api/admin/pending-grants`, { headers })
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data.users);
      setPendingGrants(grantsRes.data.grants);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Acceso denegado');
        navigate('/dashboard');
      }
    }
    setLoading(false);
  };

  // Whitelist an email for free entrepreneur-tier access — claimed the next time
  // their subscription status is checked (or, at the latest, on first MeLi connect)
  const handleCreateGrant = async () => {
    const email = grantEmail.trim().toLowerCase();
    if (!email || !email.includes('@')) { toast.error('Ingresa un email válido'); return; }
    setCreatingGrant(true);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/admin/pending-grants`,
        { email },
        { headers }
      );
      setPendingGrants(prev => [res.data.grant, ...prev.filter(g => g.email !== email)]);
      setGrantEmail('');
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al pre-aprobar acceso');
    }
    setCreatingGrant(false);
  };

  const handleDeleteGrant = async (email) => {
    setDeletingGrant(email);
    try {
      await axios.delete(`${API_BASE_URL}/api/admin/pending-grants/${encodeURIComponent(email)}`, { headers });
      setPendingGrants(prev => prev.filter(g => g.email !== email));
      toast.success('Acceso pendiente eliminado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
    setDeletingGrant(null);
  };

  const fetchHealthData = async () => {
    setLoadingHealth(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/superadmin/users-health`, { headers });
      setHealthData(res.data.users);
    } catch (error) {
      toast.error('Error al cargar datos de salud');
    }
    setLoadingHealth(false);
  };

  useEffect(() => {
    fetchData();
  }, [authToken]);

  // Toggle active status
  const handleToggleActive = async (u) => {
    const next = !u.is_active;
    const label = next ? 'activar' : 'desactivar';
    if (!window.confirm(`¿${label.charAt(0).toUpperCase() + label.slice(1)} a ${u.email}?`)) return;
    try {
      await axios.put(
        `${API_BASE_URL}/api/admin/users/${u.id}/active?is_active=${next}`,
        {},
        { headers }
      );
      setUsers(prev => prev.map(x => x.id === u.id ? { ...x, is_active: next } : x));
      toast.success(`Usuario ${next ? 'activado' : 'desactivado'}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar estado');
    }
  };

  const handleCancelDone = (result) => {
    setUsers(prev => prev.map(x => x.id === cancelTarget.id ? {
      ...x,
      plan_status: result.plan_status,
      cancel_at_period_end: result.cancel_at_period_end,
      current_period_end: result.current_period_end,
      blocked: result.blocked,
      is_active: result.is_active,
    } : x));
  };

  const handleUnblock = async (u) => {
    if (!window.confirm(`¿Desbloquear a ${u.email}? Podrá volver a iniciar sesión (su suscripción no se restaura).`)) return;
    try {
      await axios.post(`${API_BASE_URL}/api/admin/users/${u.id}/unblock`, {}, { headers });
      setUsers(prev => prev.map(x => x.id === u.id ? { ...x, blocked: false, is_active: true } : x));
      toast.success('Usuario desbloqueado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al desbloquear');
    }
  };

  // Assign a plan directly (bypasses Stripe — used for trial or manual comps)
  const handleAssignPlan = async (u, tier) => {
    if (!tier || tier === u.plan_tier) return;
    setAssigningPlan(u.id);
    try {
      await axios.put(
        `${API_BASE_URL}/api/admin/users/${u.id}/plan?tier=${tier}`,
        {},
        { headers }
      );
      setUsers(prev => prev.map(x => x.id === u.id ? { ...x, plan_tier: tier, plan_status: 'active' } : x));
      toast.success(`Plan '${tier}' asignado a ${u.email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al asignar plan');
    }
    setAssigningPlan(null);
  };

  // Impersonate user
  const handleImpersonate = async (email) => {
    setImpersonating(email);
    const result = await startImpersonation(email);
    if (result.success) {
      toast.success(`Impersonando a ${email}`);
      navigate('/dashboard');
    } else {
      toast.error(result.error);
      setImpersonating(null);
    }
  };

  // Diagnose user
  const handleDiagnose = async (email) => {
    setDiagnosing(email);
    setDiagnoseResult(null);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/superadmin/diagnose/${encodeURIComponent(email)}`, { headers });
      setDiagnoseResult(res.data);
    } catch (error) {
      toast.error('Error en diagnóstico');
    }
    setDiagnosing(null);
  };

  // Force sync
  const handleForceSync = async (email) => {
    if (!window.confirm(`Forzar sincronización completa para ${email}?`)) return;
    setSyncingUser(email);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/superadmin/force-sync/${encodeURIComponent(email)}`, {}, { headers });
      const d = res.data;
      toast.success(`Sync completado: ${d.diff?.new_products || 0} nuevos productos, ${d.diff?.new_skus || 0} nuevos SKUs`);
      if (healthData.length > 0) fetchHealthData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error en sync');
    }
    setSyncingUser(null);
  };

  // Check specific item
  const handleCheckItem = async () => {
    if (!checkItemEmail || !checkItemId) { toast.error('Ingresa email y Item ID'); return; }
    setCheckingItem(true);
    setCheckItemResult(null);
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/superadmin/check-item/${encodeURIComponent(checkItemEmail)}/${encodeURIComponent(checkItemId.trim())}`,
        { headers }
      );
      setCheckItemResult(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al verificar item');
    }
    setCheckingItem(false);
  };

  // Revenue
  const fetchUserRevenue = async (userId) => {
    setLoadingRevenue(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/admin/user-revenue/${userId}`, { headers });
      setUserRevenue(res.data);
    } catch { setUserRevenue(null); }
    setLoadingRevenue(false);
  };
  const handleViewRevenue = (u) => { setSelectedUser(u); fetchUserRevenue(u.id); };
  const closeRevenueModal = () => { setSelectedUser(null); setUserRevenue(null); };

  // Filter health data
  const filteredHealth = healthData.filter(u =>
    !searchQuery || u.email.toLowerCase().includes(searchQuery.toLowerCase()) || (u.name || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} className="text-slate-400 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                <Shield className="w-6 h-6 text-blue-400" />
                Panel Superadmin
              </h1>
              <p className="text-slate-400 text-sm">Gestión, diagnóstico e impersonación de usuarios</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => { fetchData(); if (activeTab === 'health') fetchHealthData(); }} className="border-slate-700 text-slate-300">
              <RefreshCw className="w-4 h-4 mr-2" /> Actualizar
            </Button>
            <Button variant="outline" size="sm" onClick={logout} className="border-red-800 text-red-400 hover:bg-red-900/20 hover:text-red-300">
              <LogOut className="w-4 h-4 mr-2" /> Cerrar sesión
            </Button>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <StatCard title="Total Usuarios" value={stats.total_users} icon={Users} color="indigo" />
            <StatCard title="MeLi Conectado" value={stats.meli_connected} icon={CheckCircle} color="yellow" />
            <StatCard title="VIP Users" value={stats.users_by_role?.vip_user || 0} icon={Shield} color="purple" />
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-slate-900/50 p-1 rounded-lg w-fit">
          {[
            { id: 'users', label: 'Usuarios', icon: Users },
            { id: 'health', label: 'Salud & Diagnóstico', icon: Activity },
            { id: 'support', label: 'Casos de Soporte', icon: LifeBuoy },
            { id: 'cancellations', label: 'Cancelaciones', icon: UserMinus },
            { id: 'notifications', label: 'Notificaciones', icon: Bell },
            { id: 'affiliates', label: 'Consultores', icon: Handshake },
            { id: 'query', label: 'Consultas DB', icon: Terminal },
          ].map(tab => (
            <button
              key={tab.id}
              data-testid={`tab-${tab.id}`}
              onClick={() => { setActiveTab(tab.id); if (tab.id === 'health' && healthData.length === 0) fetchHealthData(); }}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === tab.id ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-800'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* TAB: Users */}
        {activeTab === 'users' && (
          <>
          {/* Free access whitelist — pre-approve an email for free entrepreneur-tier access.
              User still registers normally; claimed the next time their subscription is checked. */}
          <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6 mb-6">
            <h2 className="text-lg font-semibold text-white mb-1 flex items-center gap-2">
              <Gift className="w-5 h-5 text-emerald-400" /> Accesos Gratuitos Pre-aprobados
            </h2>
            <p className="text-slate-400 text-xs mb-4">
              Agrega el email de un usuario para pre-aprobarlo con acceso gratuito (plan Entrepreneur). El usuario
              igual debe crear su cuenta normalmente (nombre + contraseña); el acceso se activa automáticamente
              en cuanto se verifica su suscripción — sin pasar por Stripe.
            </p>
            <div className="flex flex-wrap gap-2 items-center mb-4">
              <Input
                value={grantEmail}
                onChange={(e) => setGrantEmail(e.target.value)}
                placeholder="email@ejemplo.com"
                className="bg-slate-800/50 border-slate-700 text-slate-200 text-sm max-w-xs"
                data-testid="grant-email-input"
              />
              <Button
                onClick={handleCreateGrant}
                disabled={creatingGrant || !grantEmail.trim()}
                className="bg-emerald-600 hover:bg-emerald-500 text-white text-sm"
                data-testid="grant-create-btn"
              >
                {creatingGrant ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Gift className="w-4 h-4 mr-1" />}
                Pre-aprobar
              </Button>
            </div>

            {pendingGrants.length > 0 && (
              <div className="space-y-1.5 max-h-[30vh] overflow-y-auto">
                {pendingGrants.map((g) => (
                  <div key={g.email} className="flex items-center justify-between px-3 py-2 bg-slate-800/30 rounded-lg text-sm">
                    <div className="flex items-center gap-2 text-slate-300">
                      <Mail className="w-3.5 h-3.5 text-slate-500" />
                      {g.email}
                      <span className="text-xs bg-indigo-500/20 text-indigo-400 px-2 py-0.5 rounded">{g.tier}</span>
                      {g.status === 'claimed' ? (
                        <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" /> Reclamado
                        </span>
                      ) : (
                        <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded">Pendiente</span>
                      )}
                    </div>
                    {g.status === 'pending' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteGrant(g.email)}
                        disabled={deletingGrant === g.email}
                        className="border-red-500/40 text-red-400 hover:bg-red-500/10 text-xs h-7"
                      >
                        {deletingGrant === g.email ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-400" /> Usuarios Registrados
            </h2>
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              {users.map((u) => (
                <div key={u.id} className="flex items-center justify-between p-3 bg-slate-800/30 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
                      u.role === 'super_admin' ? 'bg-cyan-500/20 text-cyan-400' :
                      u.role === 'vip_user' ? 'bg-yellow-500/20 text-yellow-400' :
                      'bg-slate-500/20 text-slate-400'
                    }`}>
                      {u.name?.charAt(0)?.toUpperCase() || u.email?.charAt(0)?.toUpperCase()}
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium">{u.name || 'Sin nombre'}</p>
                      <p className="text-slate-400 text-xs">{u.email}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          u.role === 'super_admin' ? 'bg-cyan-500/20 text-cyan-400' :
                          u.role === 'vip_user' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-slate-500/20 text-slate-400'
                        }`}>{u.role}</span>
                        {u.meli_connected ? (
                          <span className="text-xs text-green-400 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> MeLi</span>
                        ) : (
                          <span className="text-xs text-slate-500 flex items-center gap-1"><XCircle className="w-3 h-3" /> Sin MeLi</span>
                        )}
                        {u.is_active === false ? (
                          <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded flex items-center gap-1">
                            <UserX className="w-3 h-3" /> Inactivo
                          </span>
                        ) : (
                          <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded flex items-center gap-1">
                            <UserCheck className="w-3 h-3" /> Activo
                          </span>
                        )}
                        {u.blocked && (
                          <span className="text-xs bg-red-600/20 text-red-400 px-2 py-0.5 rounded flex items-center gap-1">
                            <Lock className="w-3 h-3" /> Bloqueado
                          </span>
                        )}
                        {u.cancel_at_period_end && u.plan_status !== 'canceled' && u.current_period_end && (
                          <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded">
                            Cancela el {new Date(u.current_period_end).toLocaleDateString('es-CO')}
                          </span>
                        )}
                        {u.plan_tier && u.plan_status === 'active' ? (
                          <span className="text-xs bg-indigo-500/20 text-indigo-400 px-2 py-0.5 rounded flex items-center gap-1">
                            <CreditCard className="w-3 h-3" /> {u.plan_tier}
                          </span>
                        ) : (
                          <span className="text-xs text-slate-500 flex items-center gap-1">
                            <CreditCard className="w-3 h-3" /> Sin plan
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <select
                      value=""
                      onChange={(e) => handleAssignPlan(u, e.target.value)}
                      disabled={assigningPlan === u.id}
                      className="bg-slate-800/50 border border-slate-700 text-slate-300 text-xs rounded px-2 py-1.5 focus:outline-none focus:border-blue-500"
                      title="Asignar plan manualmente"
                    >
                      <option value="" disabled>Asignar plan...</option>
                      {PLAN_TIERS.map((tier) => (
                        <option key={tier} value={tier}>{tier}</option>
                      ))}
                    </select>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleToggleActive(u)}
                      className={u.is_active === false
                        ? 'border-green-500/50 text-green-400 hover:bg-green-500/10 text-xs'
                        : 'border-red-500/50 text-red-400 hover:bg-red-500/10 text-xs'
                      }
                      title={u.is_active === false ? 'Activar usuario' : 'Desactivar usuario'}
                    >
                      {u.is_active === false
                        ? <><UserCheck className="w-3.5 h-3.5 mr-1" /> Activar</>
                        : <><UserX className="w-3.5 h-3.5 mr-1" /> Desactivar</>
                      }
                    </Button>
                    {u.role !== 'super_admin' && (u.blocked ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleUnblock(u)}
                        className="border-green-500/50 text-green-400 hover:bg-green-500/10 text-xs"
                        title="Permitir que vuelva a iniciar sesión"
                      >
                        <Unlock className="w-3.5 h-3.5 mr-1" /> Desbloquear
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCancelTarget(u)}
                        data-testid={`admin-cancel-btn-${u.email}`}
                        className="border-red-600/60 text-red-500 hover:bg-red-600/10 text-xs"
                        title="Cancelar suscripción en Stripe y/o bloquear la cuenta"
                      >
                        <Ban className="w-3.5 h-3.5 mr-1" /> Cancelar
                      </Button>
                    ))}
                    {u.meli_connected && (
                      <>
                        <Button variant="outline" size="sm" onClick={() => handleViewRevenue(u)} className="border-slate-700 text-slate-300 text-xs">
                          <DollarSign className="w-3.5 h-3.5 mr-1" /> Ingresos
                        </Button>
                        <Button
                          data-testid={`impersonate-btn-${u.email}`}
                          variant="outline"
                          size="sm"
                          onClick={() => handleImpersonate(u.email)}
                          disabled={impersonating === u.email}
                          className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10 text-xs"
                        >
                          {impersonating === u.email ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <LogIn className="w-3.5 h-3.5 mr-1" />}
                          Entrar
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <AdminCancelUserDialog
            user={cancelTarget}
            open={Boolean(cancelTarget)}
            onOpenChange={(next) => { if (!next) setCancelTarget(null); }}
            headers={headers}
            onDone={handleCancelDone}
          />
          </>
        )}

        {/* TAB: Health & Diagnostics */}
        {activeTab === 'health' && (
          <div className="space-y-4">
            {/* Search */}
            <div className="flex items-center gap-3">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  data-testid="health-search"
                  placeholder="Buscar por email o nombre..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-slate-800/50 border-slate-700 text-white"
                />
              </div>
              <Button variant="outline" size="sm" onClick={fetchHealthData} disabled={loadingHealth} className="border-slate-700 text-slate-300">
                {loadingHealth ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              </Button>
            </div>

            {loadingHealth ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
              </div>
            ) : (
              <div className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-800">
                        <th className="text-left p-3 text-slate-400 font-medium">Usuario</th>
                        <th className="text-center p-3 text-slate-400 font-medium">MeLi</th>
                        <th className="text-center p-3 text-slate-400 font-medium">Productos</th>
                        <th className="text-center p-3 text-slate-400 font-medium">SKUs</th>
                        <th className="text-center p-3 text-slate-400 font-medium">Ordenes</th>
                        <th className="text-center p-3 text-slate-400 font-medium">Sync</th>
                        <th className="text-right p-3 text-slate-400 font-medium">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredHealth.map((u) => (
                        <tr key={u.id} className="border-b border-slate-800/50 hover:bg-slate-800/20">
                          <td className="p-3">
                            <p className="text-white font-medium">{u.name || 'Sin nombre'}</p>
                            <p className="text-slate-500 text-xs">{u.email}</p>
                          </td>
                          <td className="p-3 text-center">
                            {u.has_meli_token ? (
                              <CheckCircle className="w-4 h-4 text-green-400 mx-auto" />
                            ) : (
                              <XCircle className="w-4 h-4 text-red-400 mx-auto" />
                            )}
                          </td>
                          <td className="p-3 text-center">
                            <span className="text-white">{u.total_products}</span>
                          </td>
                          <td className="p-3 text-center">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              u.sku_pct >= 80 ? 'bg-green-500/20 text-green-400' :
                              u.sku_pct >= 50 ? 'bg-yellow-500/20 text-yellow-400' :
                              u.total_products === 0 ? 'bg-slate-500/20 text-slate-400' :
                              'bg-red-500/20 text-red-400'
                            }`}>
                              {u.products_with_sku}/{u.total_products} ({u.sku_pct}%)
                            </span>
                          </td>
                          <td className="p-3 text-center text-white">{u.cached_orders}</td>
                          <td className="p-3 text-center">
                            <span className={`text-xs ${
                              u.sync_status === 'completed' ? 'text-green-400' :
                              u.sync_status === 'running' ? 'text-blue-400' :
                              u.sync_status === 'error' ? 'text-red-400' :
                              'text-slate-500'
                            }`}>
                              {u.sync_status === 'never' ? 'Nunca' : u.sync_status}
                            </span>
                          </td>
                          <td className="p-3">
                            <div className="flex items-center justify-end gap-1">
                              {u.has_meli_token && (
                                <>
                                  <Button
                                    data-testid={`health-impersonate-${u.email}`}
                                    variant="ghost" size="sm"
                                    onClick={() => handleImpersonate(u.email)}
                                    disabled={impersonating === u.email}
                                    className="text-amber-400 hover:bg-amber-500/10 h-8 px-2"
                                    title="Entrar como usuario"
                                  >
                                    {impersonating === u.email ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LogIn className="w-3.5 h-3.5" />}
                                  </Button>
                                  <Button
                                    data-testid={`diagnose-${u.email}`}
                                    variant="ghost" size="sm"
                                    onClick={() => handleDiagnose(u.email)}
                                    disabled={diagnosing === u.email}
                                    className="text-cyan-400 hover:bg-cyan-500/10 h-8 px-2"
                                    title="Diagnosticar"
                                  >
                                    {diagnosing === u.email ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Stethoscope className="w-3.5 h-3.5" />}
                                  </Button>
                                  <Button
                                    data-testid={`force-sync-${u.email}`}
                                    variant="ghost" size="sm"
                                    onClick={() => handleForceSync(u.email)}
                                    disabled={syncingUser === u.email}
                                    className="text-blue-400 hover:bg-blue-500/10 h-8 px-2"
                                    title="Forzar Sync"
                                  >
                                    {syncingUser === u.email ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                                  </Button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Item Check Tool */}
            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5 mt-4">
              <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                <Search className="w-4 h-4 text-cyan-400" /> Investigar Item Específico
              </h3>
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <label className="text-xs text-slate-500 mb-1 block">Email del usuario</label>
                  <select
                    data-testid="check-item-email"
                    value={checkItemEmail}
                    onChange={(e) => setCheckItemEmail(e.target.value)}
                    className="w-full bg-slate-800/50 border border-slate-700 text-white rounded-md px-3 py-2 text-sm"
                  >
                    <option value="">Seleccionar usuario...</option>
                    {healthData.filter(u => u.has_meli_token).map(u => (
                      <option key={u.id} value={u.email}>{u.email}</option>
                    ))}
                  </select>
                </div>
                <div className="flex-1">
                  <label className="text-xs text-slate-500 mb-1 block">Item ID (ej: MLM1928189415)</label>
                  <Input
                    data-testid="check-item-id"
                    placeholder="MLM1928189415"
                    value={checkItemId}
                    onChange={(e) => setCheckItemId(e.target.value)}
                    className="bg-slate-800/50 border-slate-700 text-white"
                  />
                </div>
                <Button
                  data-testid="check-item-btn"
                  onClick={handleCheckItem}
                  disabled={checkingItem || !checkItemEmail || !checkItemId}
                  className="bg-cyan-600 hover:bg-cyan-500 text-white"
                >
                  {checkingItem ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  <span className="ml-2">Verificar</span>
                </Button>
              </div>

              {/* Check Item Result */}
              {checkItemResult && (
                <div className="mt-4 space-y-3">
                  <div className={`p-3 rounded-lg border text-sm ${
                    checkItemResult.checks?.in_db?.found ? 'border-green-500/30 bg-green-500/5' :
                    checkItemResult.checks?.in_search?.found ? 'border-yellow-500/30 bg-yellow-500/5' :
                    'border-red-500/30 bg-red-500/5'
                  }`}>
                    <p className="font-medium text-white mb-1">Diagnóstico: {checkItemResult.item_id}</p>
                    <p className="text-slate-300">{checkItemResult.diagnosis}</p>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                    <div className={`p-2 rounded-lg ${checkItemResult.checks?.in_db?.found ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
                      En DB: {checkItemResult.checks?.in_db?.found ? `Si (${checkItemResult.checks.in_db.count})` : 'No'}
                    </div>
                    <div className={`p-2 rounded-lg ${checkItemResult.checks?.in_search?.found ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
                      En búsqueda: {checkItemResult.checks?.in_search?.found ? 'Si' : 'No'} ({checkItemResult.checks?.in_search?.total_items_in_meli || 0} total)
                    </div>
                    <div className={`p-2 rounded-lg ${checkItemResult.checks?.multiget?.item_code === 200 ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
                      Multiget: {checkItemResult.checks?.multiget?.item_code === 200 ? 'OK' : `Error ${checkItemResult.checks?.multiget?.item_code || '?'}`}
                    </div>
                    <div className={`p-2 rounded-lg ${checkItemResult.checks?.single_item_api?.http_status === 200 ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
                      API directa: {checkItemResult.checks?.single_item_api?.http_status === 200 ? 'OK' : `Error ${checkItemResult.checks?.single_item_api?.http_status || '?'}`}
                    </div>
                  </div>

                  {checkItemResult.checks?.multiget?.item_detail && (
                    <div className="bg-slate-800/50 rounded-lg p-3 text-xs space-y-1">
                      <p className="text-white font-medium">{checkItemResult.checks.multiget.item_detail.title}</p>
                      <div className="grid grid-cols-2 gap-1 text-slate-400">
                        <span>Status: <span className="text-white">{checkItemResult.checks.multiget.item_detail.status}</span></span>
                        <span>Precio: <span className="text-white">${checkItemResult.checks.multiget.item_detail.price?.toLocaleString()}</span></span>
                        <span>SKU: <span className="text-white">{checkItemResult.checks.multiget.item_detail.seller_sku || checkItemResult.checks.multiget.item_detail.attr_sku || 'N/A'}</span></span>
                        <span>Logística: <span className="text-white">{checkItemResult.checks.multiget.item_detail.logistic_type || 'N/A'}</span></span>
                        <span>Variaciones: <span className="text-white">{checkItemResult.checks.multiget.item_detail.variations_count}</span></span>
                        <span>Catálogo: <span className="text-white">{checkItemResult.checks.multiget.item_detail.catalog_listing ? 'Sí' : 'No'}</span></span>
                      </div>
                    </div>
                  )}

                  {checkItemResult.checks?.multiget?.variations?.length > 0 && (
                    <div className="bg-slate-800/50 rounded-lg p-3 text-xs">
                      <p className="text-slate-400 font-medium mb-2">Variaciones:</p>
                      {checkItemResult.checks.multiget.variations.map((v, i) => (
                        <div key={i} className="flex items-center gap-3 py-1 border-b border-slate-700/50 last:border-0">
                          <span className="text-slate-500">ID: {v.variation_id}</span>
                          <span className="text-white">{v.attributes?.map(a => a.value).join(' / ')}</span>
                          <span className="text-slate-400">SKU: {v.seller_sku || 'N/A'}</span>
                          <span className="text-slate-400">Stock: {v.available_quantity}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Diagnose Result Modal */}
            {diagnoseResult && (
              <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setDiagnoseResult(null)}>
                <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-3xl max-h-[80vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-between p-5 border-b border-slate-700">
                    <h3 className="text-lg font-bold text-white flex items-center gap-2">
                      <Stethoscope className="w-5 h-5 text-cyan-400" />
                      Diagnóstico: {diagnoseResult.email}
                    </h3>
                    <Button variant="ghost" size="icon" onClick={() => setDiagnoseResult(null)} className="text-slate-400 hover:text-white">
                      <X className="w-5 h-5" />
                    </Button>
                  </div>
                  <div className="p-5 overflow-y-auto max-h-[65vh] space-y-4">
                    {/* OAuth */}
                    <DiagnoseSection title="OAuth" status={diagnoseResult.sections?.oauth?.status === 'ok' ? 'ok' : 'error'}>
                      <p className="text-slate-300 text-sm">{diagnoseResult.sections?.oauth?.status === 'ok' ? 'Token OAuth válido' : diagnoseResult.sections?.oauth?.message}</p>
                    </DiagnoseSection>

                    {/* Products */}
                    {diagnoseResult.sections?.products_db && (
                      <DiagnoseSection title="Productos en DB" status={diagnoseResult.sections.products_db.total > 0 ? 'ok' : 'warning'}>
                        <div className="grid grid-cols-3 gap-3 text-sm">
                          <div><span className="text-slate-500">Total:</span> <span className="text-white">{diagnoseResult.sections.products_db.total}</span></div>
                          <div><span className="text-slate-500">Con SKU:</span> <span className="text-green-400">{diagnoseResult.sections.products_db.with_sku}</span></div>
                          <div><span className="text-slate-500">Sin SKU:</span> <span className="text-red-400">{diagnoseResult.sections.products_db.without_sku}</span></div>
                        </div>
                        {diagnoseResult.sections.products_db.samples_without_sku?.length > 0 && (
                          <div className="mt-2">
                            <p className="text-xs text-slate-500 mb-1">Productos sin SKU (muestra):</p>
                            {diagnoseResult.sections.products_db.samples_without_sku.map((p, i) => (
                              <p key={i} className="text-xs text-slate-400 truncate">{p.item_id} - {p.title}</p>
                            ))}
                          </div>
                        )}
                      </DiagnoseSection>
                    )}

                    {/* MeLi API */}
                    {diagnoseResult.sections?.meli_api && (
                      <DiagnoseSection 
                        title="MeLi API vs DB" 
                        status={diagnoseResult.sections.meli_api.items_not_synced === 0 ? 'ok' : 'warning'}
                      >
                        <div className="grid grid-cols-3 gap-3 text-sm">
                          <div><span className="text-slate-500">En MeLi:</span> <span className="text-white">{diagnoseResult.sections.meli_api.total_items_in_meli}</span></div>
                          <div><span className="text-slate-500">En DB:</span> <span className="text-white">{diagnoseResult.sections.meli_api.items_in_db}</span></div>
                          <div><span className="text-slate-500">Sin sincronizar:</span> <span className={diagnoseResult.sections.meli_api.items_not_synced > 0 ? 'text-red-400' : 'text-green-400'}>{diagnoseResult.sections.meli_api.items_not_synced}</span></div>
                        </div>
                      </DiagnoseSection>
                    )}

                    {/* Orders */}
                    {diagnoseResult.sections?.orders && (
                      <DiagnoseSection title="Ordenes en caché" status="ok">
                        <p className="text-sm text-white">{diagnoseResult.sections.orders.cached_orders} órdenes cacheadas</p>
                      </DiagnoseSection>
                    )}

                    {/* Sync */}
                    {diagnoseResult.sections?.sync && (
                      <DiagnoseSection title="Metadata de Sync" status={diagnoseResult.sections.sync.status === 'completed' ? 'ok' : 'warning'}>
                        <pre className="text-xs text-slate-400 bg-slate-800/50 p-2 rounded overflow-x-auto max-h-40">
                          {JSON.stringify(diagnoseResult.sections.sync, null, 2)}
                        </pre>
                      </DiagnoseSection>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* TAB: Support Cases */}
        {activeTab === 'support' && (
          <SupportCasesPanel authToken={authToken} />
        )}

        {/* TAB: Cancelled Subscriptions */}
        {activeTab === 'cancellations' && (
          <CancelledSubscriptionsPanel authToken={authToken} />
        )}

        {/* TAB: Consultant affiliates */}
        {activeTab === 'affiliates' && (
          <AffiliatesPanel authToken={authToken} />
        )}

        {/* TAB: Login Notifications */}
        {activeTab === 'notifications' && (
          <NotificationsPanel authToken={authToken} />
        )}

        {/* TAB: MongoDB Query Explorer */}
        {activeTab === 'query' && (
          <div className="space-y-4">
            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-white mb-1 flex items-center gap-2">
                <Terminal className="w-5 h-5 text-emerald-400" />
                Explorador de Consultas MongoDB
              </h2>
              <p className="text-slate-500 text-sm mb-5">Solo lectura · Máx. 500 documentos por consulta</p>

              {/* Controls row */}
              <div className="flex flex-wrap gap-3 mb-4">
                <input
                  type="text"
                  placeholder="Colección (ej. users, orders_cache)"
                  value={queryCollection}
                  onChange={e => { setQueryCollection(e.target.value); setQueryResult(null); setQueryError(''); }}
                  className="flex-1 min-w-[220px] bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
                />
                <select
                  value={queryOperation}
                  onChange={e => handleOperationChange(e.target.value)}
                  className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
                >
                  <option value="find">find</option>
                  <option value="aggregate">aggregate</option>
                  <option value="count">count</option>
                  <option value="distinct">distinct</option>
                </select>
                <Button
                  onClick={handleRunQuery}
                  disabled={queryRunning}
                  className="bg-emerald-600 hover:bg-emerald-500 text-white"
                >
                  {queryRunning ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Terminal className="w-4 h-4 mr-2" />}
                  Ejecutar
                </Button>
              </div>

              {/* Query body editor */}
              <textarea
                value={queryBody}
                onChange={e => { setQueryBody(e.target.value); setQueryError(''); }}
                rows={10}
                spellCheck={false}
                className="w-full bg-slate-950 border border-slate-700 rounded-lg p-4 text-sm text-emerald-300 font-mono focus:outline-none focus:border-blue-500 resize-y"
                placeholder={getQueryPlaceholder(queryOperation)}
              />

              {/* Operation hint */}
              <p className="text-slate-600 text-xs mt-1 font-mono">
                {queryOperation === 'find' && '{ filter, projection?, sort?, limit? }'}
                {queryOperation === 'aggregate' && '[ { $stage }, ... ]   — pipeline como array raíz'}
                {queryOperation === 'count' && '{ filter }'}
                {queryOperation === 'distinct' && '{ field, filter? }'}
              </p>
            </div>

            {/* Error */}
            {queryError && (
              <div className="bg-red-950/40 border border-red-800 rounded-xl p-4 flex items-start gap-3">
                <XCircle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                <p className="text-red-300 text-sm font-mono break-all">{queryError}</p>
              </div>
            )}

            {/* Results */}
            {queryResult && (
              <div className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden">
                {/* Results header */}
                <div className="flex items-center justify-between px-5 py-3 border-b border-slate-800">
                  <span className="text-slate-300 text-sm">
                    <span className="text-white font-semibold">{queryResult.count}</span> documento(s)
                  </span>
                  <div className="flex items-center gap-2">
                    {/* View toggle */}
                    <div className="flex bg-slate-800 rounded-lg p-0.5 text-xs">
                      {['table', 'json'].map(mode => (
                        <button
                          key={mode}
                          onClick={() => setQueryViewMode(mode)}
                          className={`px-3 py-1 rounded-md transition-colors ${
                            queryViewMode === mode ? 'bg-slate-600 text-white' : 'text-slate-400 hover:text-white'
                          }`}
                        >
                          {mode === 'table' ? 'Tabla' : 'JSON'}
                        </button>
                      ))}
                    </div>
                    {/* Download buttons */}
                    {queryResult.results?.length > 0 && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => downloadQueryResult('json')}
                          className="border-slate-700 text-slate-300 text-xs h-7 px-2"
                        >
                          <Download className="w-3 h-3 mr-1" /> JSON
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => downloadQueryResult('csv')}
                          className="border-slate-700 text-slate-300 text-xs h-7 px-2"
                        >
                          <Download className="w-3 h-3 mr-1" /> CSV
                        </Button>
                      </>
                    )}
                  </div>
                </div>

                {/* Results body */}
                {queryResult.results?.length === 0 ? (
                  <p className="text-slate-500 text-sm text-center py-10">Sin resultados</p>
                ) : queryViewMode === 'json' ? (
                  <pre className="text-xs text-emerald-300 font-mono p-5 overflow-auto max-h-[500px] bg-slate-950">
                    {JSON.stringify(queryResult.results, null, 2)}
                  </pre>
                ) : (
                  <div className="overflow-auto max-h-[500px]">
                    <table className="w-full text-xs text-left">
                      <thead className="sticky top-0 bg-slate-800">
                        <tr>
                          {[...new Set(queryResult.results.flatMap(Object.keys))].map(col => (
                            <th key={col} className="px-3 py-2 text-slate-400 font-medium whitespace-nowrap border-b border-slate-700">
                              {col}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {queryResult.results.map((row, i) => {
                          const cols = [...new Set(queryResult.results.flatMap(Object.keys))];
                          return (
                            <tr key={i} className={i % 2 === 0 ? 'bg-slate-900/30' : 'bg-slate-800/20'}>
                              {cols.map(col => {
                                const v = row[col];
                                const display = v === null || v === undefined
                                  ? <span className="text-slate-600">null</span>
                                  : typeof v === 'object'
                                    ? <span className="text-slate-400 italic">{JSON.stringify(v)}</span>
                                    : <span className="text-slate-200">{String(v)}</span>;
                                return (
                                  <td key={col} className="px-3 py-1.5 border-b border-slate-800/50 max-w-[300px] truncate">
                                    {display}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Revenue Modal */}
        {selectedUser && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
              <div className="flex items-center justify-between p-6 border-b border-slate-700">
                <div>
                  <h3 className="text-xl font-bold text-white flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-green-400" />
                    Ingresos de {selectedUser.name || selectedUser.email}
                  </h3>
                  <p className="text-slate-400 text-sm">{selectedUser.email}</p>
                </div>
                <Button variant="ghost" size="icon" onClick={closeRevenueModal} className="text-slate-400 hover:text-white">
                  <X className="w-5 h-5" />
                </Button>
              </div>
              <div className="p-6 overflow-y-auto max-h-[60vh]">
                {loadingRevenue ? (
                  <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 text-blue-400 animate-spin" /></div>
                ) : userRevenue ? (
                  <div className="space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-slate-800/50 rounded-lg p-4">
                        <p className="text-slate-400 text-sm">Total (12 meses)</p>
                        <p className="text-2xl font-bold text-green-400">${userRevenue.total_revenue?.toLocaleString() || 0}</p>
                      </div>
                      <div className="bg-slate-800/50 rounded-lg p-4">
                        <p className="text-slate-400 text-sm">Órdenes Totales</p>
                        <p className="text-2xl font-bold text-blue-400">{userRevenue.total_orders?.toLocaleString() || 0}</p>
                      </div>
                    </div>
                    <div>
                      <h4 className="text-white font-semibold mb-3 flex items-center gap-2"><Calendar className="w-4 h-4" /> Desglose Mensual</h4>
                      <div className="space-y-2">
                        {userRevenue.monthly_data?.length > 0 ? userRevenue.monthly_data.map((month, i) => (
                          <div key={i} className="flex items-center justify-between p-3 bg-slate-800/30 rounded-lg">
                            <span className="text-slate-300">{month.month}</span>
                            <div className="text-right">
                              <p className="text-green-400 font-medium">${month.revenue?.toLocaleString() || 0}</p>
                              <p className="text-slate-500 text-xs">{month.orders || 0} órdenes</p>
                            </div>
                          </div>
                        )) : (
                          <p className="text-slate-500 text-center py-4">No hay datos disponibles</p>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-12">No se pudieron cargar los ingresos</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Helper Components
const StatCard = ({ title, value, icon: Icon, color }) => {
  const colors = {
    indigo: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    green: 'bg-green-500/10 text-green-400 border-green-500/20',
    yellow: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
    purple: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
  };
  return (
    <div className={`p-4 rounded-xl border ${colors[color]}`}>
      <div className="flex items-center justify-between mb-2">
        <Icon className="w-5 h-5" />
        <span className="text-2xl font-bold text-white">{value}</span>
      </div>
      <p className="text-sm text-slate-400">{title}</p>
    </div>
  );
};

const DiagnoseSection = ({ title, status, children }) => {
  const statusColors = {
    ok: 'border-green-500/30 bg-green-500/5',
    warning: 'border-yellow-500/30 bg-yellow-500/5',
    error: 'border-red-500/30 bg-red-500/5',
  };
  const statusIcons = {
    ok: <CheckCircle className="w-4 h-4 text-green-400" />,
    warning: <AlertTriangle className="w-4 h-4 text-yellow-400" />,
    error: <XCircle className="w-4 h-4 text-red-400" />,
  };
  return (
    <div className={`border rounded-lg p-4 ${statusColors[status]}`}>
      <div className="flex items-center gap-2 mb-2">
        {statusIcons[status]}
        <h4 className="text-white font-medium text-sm">{title}</h4>
      </div>
      {children}
    </div>
  );
};

export default AdminPanel;
